import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  FileText,
  Activity,
  Building2,
  Users,
  MessageSquare,
  Shield,
  ShieldOff,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { dashboardApi } from "../services/dashboardApi";

const SuperAdminHomepage = () => {
  const { translate } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();
    (async () => {
      try {
        const stats = await dashboardApi.getSuperAdminStats();
        if (!abortController.signal.aborted) {
          setDashboardStats(stats);
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error("Error fetching super admin data:", err);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    })();
    return () => abortController.abort();
  }, []);

  const tenants = dashboardStats?.tenants || [];
  const activeTenants = dashboardStats?.active_tenants || 0;
  const totalAccounts = dashboardStats?.total_accounts || 0;
  const totalChats = dashboardStats?.total_chats || 0;
  const recentTenants = tenants.slice(0, 6);

  return (
    <div className="page-enter">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("welcomeBack") || "Welcome back"}
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
          {translate("superAdminDashboard") || "Platform overview and tenant statistics"}
        </p>
      </div>

      {loading ? (
        <div className="space-y-6 animate-pulse">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="card p-5">
                <div className="h-3 w-20 rounded bg-[var(--color-skeleton)] mb-3" />
                <div className="h-7 w-16 rounded bg-[var(--color-skeleton-light)] mb-2" />
                <div className="h-2 w-24 rounded bg-[var(--color-skeleton-light)]" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="card p-5">
                <div className="h-4 w-40 rounded bg-[var(--color-skeleton)] mb-4" />
                <div className="space-y-3">
                  {[...Array(3)].map((_, j) => (
                    <div key={j} className="h-12 rounded-lg bg-[var(--color-skeleton-light)]" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label={translate("totalTenants") || "Total Tenants"}
              value={tenants.length}
              icon={Building2}
            />
            <KpiCard
              label={translate("activeTenants") || "Active Tenants"}
              value={activeTenants}
              icon={Shield}
              status="success"
            />
            <KpiCard
              label={translate("totalAccountsSA") || "Total Accounts"}
              value={totalAccounts}
              icon={Users}
            />
            <KpiCard
              label={translate("totalChatsSA") || "Total Chats"}
              value={totalChats}
              icon={MessageSquare}
            />
          </div>

          {/* Three-column row — Usage Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <UsageOverviewColumn
              title={translate("usageOverviewMessages") || "Usage Overview — Messages"}
              icon={MessageSquare}
              tenants={tenants}
              usedKey="monthly_messages_used"
              limitKey="monthly_message_limit"
            />
            <UsageOverviewColumn
              title={translate("usageOverviewDocuments") || "Usage Overview — Documents"}
              icon={FileText}
              tenants={tenants}
              usedKey="current_knowledge_documents"
              limitKey="max_knowledge_documents"
            />
            <UsageOverviewColumn
              title={translate("usageOverviewCharacters") || "Usage Overview — Characters"}
              icon={Activity}
              tenants={tenants}
              usedKey="current_total_characters"
              limitKey="max_total_characters"
              formatNumber
            />
          </div>

          {/* Recent Tenants */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                {translate("recentTenants") || "Recent Tenants"}
              </h3>
              <button
                onClick={() => navigate("/tenants")}
                className="text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                style={{ color: "var(--color-mint)" }}
              >
                {translate("viewAll") || "View All"}
                <ArrowRight size={12} />
              </button>
            </div>
            {recentTenants.length > 0 ? (
              <div>
                <div
                  className="grid gap-4 px-3 py-2 text-xs font-medium uppercase tracking-wider"
                  style={{
                    gridTemplateColumns: "1fr 80px 100px 80px 100px 90px",
                    color: "var(--color-text-muted)",
                    fontSize: "10px",
                  }}
                >
                  <span>{translate("name") || "Name"}</span>
                  <span>{translate("status") || "Status"}</span>
                  <span>{translate("accounts") || "Accounts"}</span>
                  <span>{translate("chats") || "Chats"}</span>
                  <span>{translate("limits") || "Limits"}</span>
                  <span className="text-right">{translate("created") || "Created"}</span>
                </div>
                <div style={{ borderTop: "1px solid var(--color-border)" }}>
                  {recentTenants.map((tenant, i) => (
                    <div
                      key={tenant.id}
                      className="hover-row grid gap-4 px-3 py-2.5 items-center transition-colors rounded-lg cursor-pointer"
                      style={{
                        gridTemplateColumns: "1fr 80px 100px 80px 100px 90px",
                        borderBottom: i < recentTenants.length - 1 ? "1px solid var(--color-border)" : "none",
                      }}
                      onClick={() => navigate(`/tenants?expand=${tenant.id}`)}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Building2 size={14} style={{ color: "var(--color-text-muted)", flexShrink: 0 }} />
                        <span className="text-xs font-medium truncate" style={{ color: "var(--color-text-primary)" }}>
                          {tenant.name}
                        </span>
                      </div>
                      <span
                        className="text-xs font-medium px-1.5 py-0.5 rounded-full text-center"
                        style={{
                          backgroundColor: tenant.is_active ? "rgba(22,163,74,0.08)" : "rgba(107,114,128,0.08)",
                          color: tenant.is_active ? "#16a34a" : "#6b7280",
                          fontSize: "10px",
                        }}
                      >
                        {tenant.is_active ? (translate("active") || "Active") : (translate("inactive") || "Inactive")}
                      </span>
                      <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                        {tenant.accounts_count || 0}
                      </span>
                      <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                        {tenant.chats_count || 0}
                      </span>
                      <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                        {tenant.limits_enabled ? (
                          <span className="flex items-center gap-1">
                            <Shield size={10} style={{ color: "var(--color-warning)" }} />
                            {translate("enabled") || "Enabled"}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <ShieldOff size={10} style={{ color: "var(--color-text-muted)" }} />
                            {translate("unlimited") || "Unlimited"}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-right" style={{ color: "var(--color-text-muted)" }}>
                        {tenant.created_at
                          ? new Date(tenant.created_at).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs py-8 text-center" style={{ color: "var(--color-text-muted)" }}>
                {translate("noTenantsYet") || "No tenants created yet"}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Sub-components ─── */

const lerp = (a, b, t) => Math.round(a + (b - a) * t);

const getBarColor = (pct) => {
  const p = pct / 100;
  let r, g, b;
  if (p < 0.5) {
    const t = p / 0.5;
    r = lerp(140, 212, t); g = lerp(184, 176, t); b = lerp(160, 120, t);
  } else {
    const t = (p - 0.5) / 0.5;
    r = lerp(212, 200, t); g = lerp(176, 120, t); b = lerp(120, 120, t);
  }
  return `rgb(${r},${g},${b})`;
};

const formatNum = (n) => n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}K` : n;

const UsageOverviewColumn = ({ title, icon: Icon, tenants, usedKey, limitKey, formatNumber }) => {
  const filtered = tenants
    .filter((t) => t.is_active && t.limits_enabled)
    .sort((a, b) => ((b[usedKey] || 0) / (b[limitKey] || 1)) - ((a[usedKey] || 0) / (a[limitKey] || 1)))
    .slice(0, 5);

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {title}
        </h3>
        <Icon size={14} style={{ color: "var(--color-text-muted)" }} />
      </div>
      <div className="space-y-4">
        {filtered.map((t) => {
          const used = t[usedKey] || 0;
          const limit = t[limitKey] || 1;
          const pct = Math.min((used / limit) * 100, 100);
          const barColor = getBarColor(pct);
          const displayUsed = formatNumber ? formatNum(used) : used;
          const displayLimit = formatNumber ? formatNum(limit) : limit;
          return (
            <div key={t.id}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium truncate" style={{ color: "var(--color-text-secondary)", maxWidth: "60%" }}>
                  {t.name}
                </span>
                <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  {displayUsed}/{displayLimit}
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${pct}%`, backgroundColor: barColor }}
                />
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-xs text-center py-6" style={{ color: "var(--color-text-muted)" }}>
            No tenants with usage limits
          </p>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, icon: Icon, status }) => {
  const statusColor =
    status === "success"
      ? "var(--color-success)"
      : status === "error"
      ? "var(--color-error)"
      : "var(--color-mint)";

  return (
    <div className="card p-5 text-left">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>
          {label}
        </span>
        <Icon size={14} style={{ color: "var(--color-text-muted)" }} />
      </div>
      <p className="text-xl font-semibold" style={{ color: status ? statusColor : "var(--color-text-primary)" }}>
        {value}
      </p>
    </div>
  );
};

export default SuperAdminHomepage;
